(ns dre.widgets.question-answering.events
  (:require
   [dre.fx :as-alias dre.fx]
   [clojure.string :as str]
   [dre.app.events :as app-events]
   [dre.config :as conf]
   [dre.i18n.utils :as i18n-utils]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.components.search-results.events :as-alias sr-events]
   [dre.network :as-alias network]
   [dre.utils :refer [stringify-keys-with-namespaces aborted-request? admin-role?]]
   [dre.utils :as utils]
   [dre.utils.query :as query-utils]
   [dre.pages.search.events :as search-events]
   [dre.shared.interface.search.blunt :as blunt]
   [dre.storage.events :as-alias storage]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))


(def db-path [:widgets :question-answering])

(def id :question-answering)

(def default-top-k 5)

(def default-disable-hyde? true)

(def default-use-dre-model? true)

(def default-focus-on-recent-work? false)

(def default-in-cs-only? false)

(def snippet-limit 5)

(defn clean-answer
  [answer]
  (let [ans (str/trim (cond (and (str/includes? answer "<think>")
                                 (not (str/includes? answer "</think>")))
                            ""
                            (str/includes? answer "</think>")
                            (last (str/split answer #"</think>"))
                            :else answer))
        ans (if (str/starts-with? ans ":")
              (str/trim (str/replace-first ans #":" ""))
              ans)]
    ans))

(defn active-articles
  [data key]
  (let [answer (or (get-in data [:answer]) "")]
    (set (remove nil? (map
                       (fn [a]
                         (if (str/index-of answer (:article/guid a))
                           (key a)
                           nil))
                       (get-in data [:articles/to-show]))))))

(def single-article-prompt-decorator
  "(Keep your response concise, restricted to a maximum of 2 sentences, and please omit any sources in your final response.)")

(def related-sources-table-columns
  [{:value "objectives"
    :label "Study Objective"
    :query "What was the primary objective of this study, and what specific hypothesis or research question were the authors aiming to address?"}
   {:value "methodology"
    :label "Methodology"
    :query "Describe the methodology of the study, including the study design, sample size, and any specific techniques or interventions used."}
   {:value "conclusion"
    :label "Conclusion"
    :query "What conclusions did the researchers draw from their findings? Were there any implications for future research, clinical practice, or policy?"}
   {:value "limitations"
    :label "Limitations"
    :query "Were there any limitations acknowledged by the researchers that could affect the interpretation of the results?"}
   {:value "results-key-findings"
    :label "Key Findings and Results"
    :query "Summarize the main findings of the study. What were the significant results, and what did the data reveal?"}
   {:value "population"
    :label "Population"
    :query "Who were the participants in this study? Include details on age, gender, health status, and any inclusion or exclusion criteria."}
   {:value "interventions"
    :label "Interventions"
    :query "What interventions were tested in the study?"}
   {:value "outcomes"
    :label "Outcomes"
    :query "What were the primary and secondary outcomes measured in the study?"}
   {:value "adverse-events"
    :label "Adverse Events"
    :query "What adverse events were associated with the interventions in this study?"}])

(def related-sources-table-columns-index
  (into {} (map (juxt :value identity)) related-sources-table-columns))

(defn settings-to-store
  [db]
  (select-keys db [:in-pubmed-only? :in-cs-only? :drevidence-model? :disable-hyde? :top-k :articles-table-selected-columns :focus-on-recent-work?]))

(defn fetch-params
  [question drevidence-model dev? base-query disable-hyde? in-pubmed-only? in-cs-only? top-k stream-answer? focus-on-recent-work?]
  {:text question
   :reranker "rrf"
   :drevidence-model drevidence-model
   :fulltext? true
   :generate-answer? true
   :stream-answer? stream-answer?
   :dev? dev?
   :prefilter-query base-query
   :disable-hyde? disable-hyde?
   :focus-on-recent-work? focus-on-recent-work?
   :sources-type (cond
                   (and in-pubmed-only? in-cs-only?)
                   "pubmed-cs"
                   in-pubmed-only?
                   "pubmed"
                  ;;  in-cs-only?
                  ;;  "cs"
                   :else
                   "all")
   :top-k top-k})

;; -- Events

(rf/reg-event-fx
 ::set-loading
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  {:db (assoc db :state :loading)}))

(rf/reg-event-fx
 ::init
 [rf/trim-v (rf/inject-cofx ::storage/get :question-answering-settings)]
 (fn
   [{:keys [db question-answering-settings]} [{:keys [after-fx last-selected-filter-key pills-api-params-fn]}]]
   (let [articles-table-selected-columns [] ;;TODO load from localstore
         params (pills-api-params-fn [id])
         question (get-in db (concat [search-events/db-path :search-query :query/question]))
         question (if question
                    (str/replace question #"(\[[a-zA-Z/\ ]+\])" "_")
                    question)
         base-query (blunt/print [(:query/pill params)])
         ;; dont use stored settings for features which are not enabled
         user (-> db app-events/db-path :user)
         admin-role? (utils/admin-role? user)
         filtered-question-answering-settings (cond-> question-answering-settings
                                                (not (conf/feature-enabled? :question-answering-in-pubmed)) (dissoc :in-pubmed-only?)
                                                (or
                                                 (not admin-role?)
                                                 (and
                                                  admin-role?
                                                  (not (conf/feature-enabled? :question-answering-model-switch?)))) (dissoc :drevidence-model?)
                                                (not (conf/feature-enabled? :question-answering-focus-on-recent-work)) (dissoc :focus-on-recent-work?)
                                                (not (conf/feature-enabled? :question-answering-top-k)) (dissoc :top-k)
                                                true (dissoc :in-cs-only?))]

     (if question
       {:db (assoc-in db db-path (merge {:vote-exist? false
                                         :articles-table-selected-columns articles-table-selected-columns
                                         :top-k (get-in db (concat db-path [:top-k]) default-top-k)
                                         :voted? false
                                         :state :loading
                                         :question question
                                         :show-related-sources? false
                                         :base-query base-query
                                         :in-pubmed-only? true
                                         :in-cs-only? default-in-cs-only?
                                         :focus-on-recent-work? default-focus-on-recent-work?
                                         :drevidence-model? default-use-dre-model?
                                         :disable-hyde? default-disable-hyde?}
                                        filtered-question-answering-settings))
        :dispatch-n [[::fetch]
                     after-fx]}
       {:db (assoc-in db db-path (merge {:vote-exist? false
                                         :articles-table-selected-columns articles-table-selected-columns
                                         :top-k (get-in db (concat db-path [:top-k]) default-top-k)
                                         :voted? false
                                         :state nil
                                         :question nil
                                         :show-related-sources? false
                                         :in-pubmed-only? true
                                         :in-cs-only? default-in-cs-only?
                                         :focus-on-recent-work? default-focus-on-recent-work?
                                         :drevidence-model? default-use-dre-model?
                                         :disable-hyde? default-disable-hyde?
                                         :base-query base-query}
                                        filtered-question-answering-settings))
        :dispatch after-fx}))))

(rf/reg-event-fx
 ::fetch
 [rf/trim-v]
 (fn
   [{db-base :db} []]
   (let [db (get-in db-base db-path)
         question (:question db)
         base-query (:base-query db)
         in-pubmed-only? (:in-pubmed-only? db)
         in-cs-only? (:in-cs-only? db)
         focus-on-recent-work? (:focus-on-recent-work? db)
         drevidence-model (if (:drevidence-model? db) "ls" "gpt")
         disable-hyde? (or (:disable-hyde? db) default-disable-hyde?)
         top-k (get db :top-k default-top-k)
         dev? (admin-role? (get-in db-base [:app :user]))]
     (if (or (not question) (empty? question))
       {:db (assoc-in db-base db-path (assoc db
                                             :vote-exist? false
                                             :voted? false
                                             :show-related-sources? false
                                             :question nil
                                             :data {}))}
       {:db (assoc-in db-base db-path (assoc db
                                             :vote-exist? false
                                             :voted? false
                                             :show-related-sources? false
                                             :state :loading
                                             :stream-id nil
                                             :data {}))
        ::network/request-with-abort [{:endpoint :widgets/question-answering
                                       :request-timeout-s 60}
                                      {:body (stringify-keys-with-namespaces
                                              (fetch-params question drevidence-model dev? base-query disable-hyde? in-pubmed-only? in-cs-only? top-k true focus-on-recent-work?))
                                       :on-success [::success]
                                       :on-failure [::failure]}]}))))


(rf/reg-event-fx
 ::success
 [rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response]}]]
  (let [to-show (get-in response [:body :article/ids])
        [_ rest] (split-at (count to-show) (get-in response [:body :article/all]))]
    {:db (cond-> (assoc-in db db-path
                           (assoc (get-in db db-path)
                                  :stream-id (get-in (:body response) [:stream-id])
                                  :highlight-stream-id (get-in (:body response) [:stream-id])
                                  :data {:answer (get-in (first (get-in (:body response) [:answer :choices])) [:message :content])
                                         :analyzed-articles-count (get-in (:body response) [:analyzed-articles-count])
                                         :highlights (get-in (:body response) [:highlights])
                                         :articles/to-show to-show
                                         :articles/tail rest
                                         :response (:body response)}))
           (not (get-in (:body response) [:stream-id])) (assoc-in (concat db-path [:state]) :success))
     :fx [[:dispatch after-fx]
          (when (and (not (get-in (:body response) [:stream-id]))
                     (= (get-in db [:search :order]) :ai-sum))
            [:dispatch [::sr-events/fetch nil nil]])
          (when (not (get-in (:body response) [:stream-id]))
            [:dispatch [::fetch-vote]])
          [:dispatch [::fetch-table-columns]]]})))


(rf/reg-event-fx
 ::failure
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db (assoc db :state :failure
                :stream-id nil
                :data nil)
     :dispatch-n [[:dre.network/default-failure opts]]})))

(rf/reg-event-fx
 ::fetch-selected
 [rf/trim-v]
 (fn
   [{db-base :db} [guid uri source column query]]
   (let [db (get-in db-base db-path)
         question (or query (:question db))
         question (str question " " single-article-prompt-decorator)
         base-query (:base-query db)
         selected-articles [{:article/uri uri
                             :article/source source
                             :article/guid guid}]
         in-pubmed-only? (:in-pubmed-only? db)
         in-cs-only? (:in-cs-only? db)
         focus-on-recent-work? (:focus-on-recent-work? db)
         drevidence-model (if (:drevidence-model? db) "ls" "gpt-fast")
         disable-hyde? (or (:disable-hyde? db) default-disable-hyde?)
         top-k (get db :top-k default-top-k)
         dev? (admin-role? (get-in db-base [:app :user]))
         articles-table-data (or (get-in db [:data :articles-table-data]) {})
         articles-table-data (assoc-in articles-table-data [(keyword guid) :state column] :loading)]
     {:db (assoc-in db-base db-path (assoc-in db [:data :articles-table-data] articles-table-data))
      ::network/request [{:endpoint :widgets/question-answering-single
                          :request-timeout-s 90}
                         {:body (stringify-keys-with-namespaces
                                 (assoc
                                  (fetch-params question drevidence-model dev? base-query disable-hyde? in-pubmed-only? in-cs-only? top-k false focus-on-recent-work?)
                                  :selected-articles selected-articles))
                          :on-success [::success-selected {:guid guid
                                                           :column column}]
                          :on-failure [::failure-selected {:guid guid
                                                           :column column}]}]})))

(rf/reg-event-fx
 ::success-selected
 [rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [guid column ::fetch-fx/response]}]]
  (let []
    {:db (assoc-in db db-path
                   (-> (get-in db db-path)
                       (assoc-in [:data :articles-table-data (keyword guid) :state column] :success)
                       (assoc-in [:data :articles-table-data (keyword guid) column] {:answer (get-in (first (get-in (:body response) [:answer :choices])) [:message :content])
                                                                                     :highlights (get-in (:body response) [:highlights])
                                                                                     :articles (get-in response [:body :article/ids])})))})))


(rf/reg-event-fx
 ::failure-selected
 [rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [guid column ::fetch-fx/response] :as opts}]]
  (let [new-db (assoc-in db db-path
                         (-> (get-in db db-path)
                             (assoc-in [:data :articles-table-data (keyword guid) :state column] :failure)))]
    (if (aborted-request? response)
      {:db new-db}
      {:db new-db
       :dispatch-n [[:dre.network/default-failure opts]]}))))

(rf/reg-event-fx
 ::answer-update
 [rf/trim-v]
 (fn
   [{db :db} [event]]
   (let [finished? (get event :finished? false)
         same-stream? (= (get-in db (concat db-path [:stream-id])) (:stream-id event))]
     {:db (cond-> db
            same-stream? (assoc-in (concat db-path [:state]) :success)
            same-stream? (assoc-in (concat db-path [:data :answer]) (:answer event))
            (and same-stream? finished?) (assoc-in (concat db-path [:stream-id]) nil))
      :fx [(when (and same-stream? finished? (= (get-in db [:search :order]) :ai-sum))
             [:dispatch [::sr-events/fetch nil nil]])
           (when (and same-stream? finished?)
             [:dispatch [::fetch-vote]])]})))

(rf/reg-event-fx
 ::answer-highlights
 [rf/trim-v]
 (fn
   [{db :db} [event]]
   (let [same-stream? (or
                       (= (get-in db (concat db-path [:stream-id])) (:stream-id event))
                       (= (get-in db (concat db-path [:highlight-stream-id])) (:stream-id event)))]
     {:db (cond-> db
            same-stream? (assoc-in (concat db-path [:highlight-stream-id]) nil)
            same-stream? (assoc-in (concat db-path [:data :highlights]) (get-in event [:highlights])))})))

(rf/reg-event-fx
 ::fetch-snippets
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} []]
   (let [[head rest] (split-at snippet-limit (get-in db [:data :articles/tail]))]
     {:db (-> db
              (assoc :state-snippets :loading)
              (assoc :show-related-sources? true))
      ::network/request-with-abort [{:endpoint :widgets/question-answering-snippets}
                                    {:body (stringify-keys-with-namespaces {:article/ids head})
                                     :on-success [::success-snippets {:rest rest}]
                                     :on-failure [::failure-snippets]}]})))

(rf/reg-event-fx
 ::success-snippets
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [rest ::fetch-fx/response]}]]
  {:db (-> db
           (assoc :state-snippets :success)
           (assoc-in [:data :articles/tail] rest)
           (assoc-in [:data :articles/to-show] (concat (get-in db [:data :articles/to-show]) (get-in response [:body :article/ids]))))
   :dispatch [::fetch-table-columns]}))

(rf/reg-event-fx
 ::fetch-table-columns
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [_]]
  (let [articles (get-in db [:data :articles/to-show] [])
        articles-table-data (get-in db [:data :articles-table-data] {})
        articles-table-selected-columns (cons :summary (get db :articles-table-selected-columns []))
        summary-data (map
                      (fn [article]
                        (map (fn [column-name]
                               (let [table-data (get articles-table-data (keyword (:article/guid article)) {})
                                     column-data (get table-data column-name)]
                                 (when (not column-data)
                                   {:article/guid (:article/guid article)
                                    :article/uri (:article/uri article)
                                    :article/source (:article/source article)
                                    :column-name column-name
                                    :query (or (:query (get related-sources-table-columns-index column-name)) nil)}))) articles-table-selected-columns))
                      articles)
        summary-data (remove nil? (flatten summary-data))]
    (when (and
           (conf/feature-enabled? :question-answering-single-ie)
           (conf/feature-enabled? :question-answering-single-ie-automatic-summary?)) {:dispatch-n (map (fn [d]
                                                                                                         [::fetch-selected (:article/guid d) (:article/uri d) (:article/source d) (:column-name d) (:query d)])
                                                                                                       summary-data)}))))


(rf/reg-event-fx
 ::failure-snippets
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db (assoc db :state-snippets :failure)
     :dispatch-n [[:dre.network/default-failure opts]]})))

(rf/reg-event-fx
 ::fetch-vote
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} []]
   (let [question (:question db)
         base-query (:base-query db)
         answer (get-in db [:data :answer])]
     {:db (-> db
              (assoc :vote-loading? true))
      ::network/request-with-abort [{:endpoint :widgets/question-answering-fetch-vote}
                                    {:body (stringify-keys-with-namespaces {:query/blunt base-query
                                                                            :question question
                                                                            :answer answer})
                                     :on-success [::success-fetch-vote]
                                     :on-failure [::failure-fetch-vote]}]})))

(rf/reg-event-fx
 ::success-fetch-vote
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [rest ::fetch-fx/response]}]]
  (let [body (:body response)
        meta (:meta body)
        voted? (when (get meta :vote)
                 true)]
    {:db (-> db
             (assoc :vote-loading? false)
             (assoc :voted? voted?)
             (assoc :vote-exist? voted?))})))

(rf/reg-event-fx
 ::failure-fetch-vote
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  {:db (assoc db :vote-loading? false)
   :dispatch-n [[:dre.network/default-failure opts]]}))

(rf/reg-event-fx
 ::vote
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} [vote vote-feedback]]
   (let [question (:question db)
         base-query (:base-query db)
         answer (get-in db [:data :answer])]
     {:db (-> db
              (assoc :voted? true))
      ::network/request [{:endpoint :widgets/question-answering-vote}
                         {:body (stringify-keys-with-namespaces {:query/blunt base-query
                                                                 :question question
                                                                 :answer answer
                                                                 :vote vote
                                                                 :vote-feedback vote-feedback})}]})))

(rf/reg-event-fx
 ::set-question
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} [question]]
   {:db (assoc db :question question)}))

(rf/reg-event-fx
 ::toggle-pubmed-filter
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} _]
   (let [new-db (update-in db [:in-pubmed-only?] not)]
     {:db new-db
      :dispatch [::fetch]
      ::storage/set [:question-answering-settings (assoc (settings-to-store db) :in-pubmed-only? (:in-pubmed-only? new-db))]})))

(rf/reg-event-fx
 ::toggle-cs-filter
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} _]
   (let [new-db (update-in db [:in-cs-only?] not)]
     {:db new-db
      :dispatch [::fetch]
      ::storage/set [:question-answering-settings (assoc (settings-to-store db) :in-cs-only? (:in-cs-only? new-db))]})))

(rf/reg-event-fx
 ::toggle-drevidence-model-filter
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} _]
   (let [new-db (update-in db [:drevidence-model?] not)]
     {:db new-db
      :dispatch [::fetch]
      ::storage/set [:question-answering-settings (assoc (settings-to-store db) :drevidence-model? (:drevidence-model? new-db))]})))

(rf/reg-event-fx
 ::toggle-hyde-filter
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} _]
   (let [new-db (update-in db [:disable-hyde?] not)]
     {:db new-db
      :dispatch [::fetch]
      ::storage/set [:question-answering-settings (assoc (settings-to-store db) :disable-hyde? (:disable-hyde? new-db))]})))

(rf/reg-event-fx
 ::toggle-focus-on-recent-work
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} _]
   (let [new-db (update-in db [:focus-on-recent-work?] not)]
     {:db new-db
      :dispatch [::fetch]
      ::storage/set [:question-answering-settings (assoc (settings-to-store db) :focus-on-recent-work? (:focus-on-recent-work? new-db))]})))

(rf/reg-event-fx
 ::set-top-k
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} [top-k]]
   {:db (assoc-in db [:top-k] top-k)
    :dispatch [::fetch]
    ::storage/set [:question-answering-settings (assoc (settings-to-store db) :top-k top-k)]}))

(defn clean-csv-string
  [text]
  (str/replace text #"\"" "“"))

(rf/reg-event-fx
 ::export-with-citations
 [rf/trim-v]
 (fn-traced
  [{root-db :db} [{:keys [::fetch-fx/response]}]]
  (let [db (get-in root-db db-path)
        i18n-db (:i18n root-db)
        articles (get-in db [:data :articles/to-show] [])
        id-by-guid (into {} (map-indexed (fn [idx article]
                                           [(:article/guid article) (str "C" (+ idx 1))]) articles))
        citations (into {} (map (fn [citation] [(last (str/split (:article/url citation) #"/"))
                                                citation])
                                (:body response)))
        articles-table-selected-columns (if (conf/feature-enabled? :question-answering-single-ie)
                                          (concat [:summary] (get db :articles-table-selected-columns []))
                                          [])
        articles-table-data (get-in db [:data :articles-table-data] {})

        answer (reduce
                (fn [answer article]
                  (let [id (get id-by-guid (:article/guid article))
                        id-str (or id "Error")
                        guid (:article/guid article)]
                    (str/replace answer guid (str "[" id-str "]"))))
                (or (get-in db [:data :answer]) "")
                (take (get-in db [:top-k] default-top-k) articles))
        answer (clean-csv-string (clean-answer answer))
        answer (str "\"" answer "\"")

        answer-header (str (i18n-utils/translate i18n-db "Summary"))
        articles-csv-lines (map (fn [article]
                                  (let [citation (get citations (:article/guid article))
                                        at-data (get articles-table-data (keyword (:article/guid article)))]
                                    (str (pr-str (get id-by-guid (:article/guid article))) ","
                                         (pr-str (clean-csv-string (:article/citation citation))) ","
                                         (pr-str (clean-csv-string (:article/title citation))) ","
                                         (:article/url citation) ","
                                         (str/join "," (map #(pr-str (clean-csv-string
                                                                      (clean-answer
                                                                       (or (:answer (get at-data %)) "-"))))
                                                            articles-table-selected-columns)))))
                                articles)
        articles-header (str (i18n-utils/translate i18n-db "ID") ","
                             (i18n-utils/translate i18n-db "Citation") ","
                             (i18n-utils/translate i18n-db "Title") ","
                             (i18n-utils/translate i18n-db "Url") ","
                             (str/join "," (map #(or (:label (get related-sources-table-columns-index %)) "Summary") articles-table-selected-columns)))
        csv (str (str/join "\n" (cons answer-header
                                      [answer]))
                 "\n\n\n"
                 (str/join "\n" (cons articles-header
                                      articles-csv-lines)))]
    (rf/dispatch [::dre.fx/download {:content csv
                                     :filename "ai-summarization-report.csv"
                                     :mimetype "text/csv"}])
    nil)))

(rf/reg-event-fx
 ::export
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} []]
   (let [articles (get-in db [:data :articles/to-show] [])
         ids (map #(select-keys % [:article/uri :article/source]) articles)]
     {::network/request-with-abort [{:endpoint :search/export-json}
                                    {:body (stringify-keys-with-namespaces {:limit (count ids)
                                                                            :ids ids})
                                     :on-success [::export-with-citations]}]})))

(rf/reg-event-fx
 ::export-dev
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} []]
   (let [json-str (.stringify js/JSON (clj->js (get db :data)))]
     (rf/dispatch [::dre.fx/download {:content json-str
                                      :filename (str "ai-sum-dev.json")
                                      :mimetype "application/json"}])
     {:dispatch [::export]})))

(rf/reg-event-fx
 ::set-articles-table-selected-columns
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} [selected]]
   (let [added? (> (count selected) (count (get db :articles-table-selected-columns [])))]
     (cond-> {:db (assoc-in db [:articles-table-selected-columns] selected)
              ::storage/set [:question-answering-settings (assoc (settings-to-store db) :articles-table-selected-columns selected)]}
       added? (assoc :dispatch [::fetch-table-columns])))))

;; -- Subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (get-in db db-path)))

(rf/reg-sub
 ::data
 :<- [::state]
 (fn [state _]
   (:data state)))

(rf/reg-sub
 ::disable-hyde?
 :<- [::state]
 (fn [state _]
   (:disable-hyde? state)))

(rf/reg-sub
 ::in-pubmed-only?
 :<- [::state]
 (fn [state _]
   (:in-pubmed-only? state)))

(rf/reg-sub
 ::in-cs-only?
 :<- [::state]
 (fn [state _]
   (:in-cs-only? state)))

(rf/reg-sub
 ::focus-on-recent-work?
 :<- [::state]
 (fn [state _]
   (:focus-on-recent-work? state)))

(rf/reg-sub
 ::drevidence-model?
 :<- [::state]
 (fn [state _]
   (:drevidence-model? state)))

(rf/reg-sub
 ::top-k
 :<- [::state]
 (fn [state _]
   (get state :top-k default-top-k)))

(rf/reg-sub
 ::question
 :<- [::state]
 (fn [state _]
   (:question state)))

(rf/reg-sub
 ::status
 :<- [::state]
 (fn [db _]
   (:state db)))

(rf/reg-sub
 ::show-related-sources?
 :<- [::state]
 (fn [state _]
   (:show-related-sources? state)))